import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, message, Space, Typography, Row, Col, Card, Dropdown, Input, Spin, Menu } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, PrinterOutlined, SearchOutlined, MoreOutlined, FileDoneOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  fetchPackageTypes,
  fetchClientName,
  fetchQuotations as fetchQuotationsService,
  deleteQuotation,
  downloadPDF,
  copyQuotation
} from '../services/quotationService';

const { Text, Title } = Typography;
const { Search } = Input;

const Quotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [clientNames, setClientNames] = useState({});
  const [searchText, setSearchText] = useState('');
  const [packageTypes, setPackageTypes] = useState([{ PackageID: 'Loading...', PackageName: 'Loading...' }]);
  const navigate = useNavigate();

  const baseUrl = import.meta.env.VITE_API_BASE_URL;

  const statusColors = {
    draft: 'default',
    sent: 'blue',
    approved: 'green',
    rejected: 'red'
  };

  const fetchPackageType = async () => {
    try {
      const packages = await fetchPackageTypes();
      setPackageTypes(packages);
    } catch (error) {
      console.error('Error fetching package types:', error);
      message.error('Failed to fetch package types');
    }
  }

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const data = await fetchQuotationsService();
      setQuotations(data);
      setFilteredQuotations(data);
      const uniqueClientIds = Array.from(new Set(data.map(q => q.UserID)));
      const clientNamesMap = {};
      await Promise.all(
        uniqueClientIds.map(async (clientId) => {
          const name = await fetchClientName(clientId);
          clientNamesMap[clientId] = name;
        })
      );
      setClientNames(clientNamesMap);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      message.error('Failed to fetch quotations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotations();
    fetchPackageType();
  }, []);

  useEffect(() => {
    const filtered = quotations.filter(q =>
      q.ProjectName.toLowerCase().includes(searchText.toLowerCase()) ||
      q.ProjectCode.toLowerCase().includes(searchText.toLowerCase()) ||
      (clientNames[q.UserID] && clientNames[q.UserID].toLowerCase().includes(searchText.toLowerCase()))
    );
    setFilteredQuotations(filtered);
  }, [searchText, quotations, clientNames]);

  const confirmDeleteQuotation = (quotation) => {
    Modal.confirm({
      title: 'Delete Quotation',
      content: `Are you sure you want to delete quotation "${quotation.ProjectName}"?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      maskStyle: { backdropFilter: 'blur(8px)' },
      onOk: async () => {
        try {
          await deleteQuotation(quotation.QuotationID);
          message.success('Quotation deleted successfully');
          fetchQuotations();
        } catch (error) {
          console.error('Error deleting quotation:', error);
          message.error('Failed to delete quotation');
        }
      },
    });
  };
  // Download PDF with spinner
  const handleDownloadPDF = async (record, isPrint) => {
    try {
      setDownloadingId(record.QuotationID);
      const response = await downloadPDF(record.QuotationID, isPrint);
      // Build filename: ClientName_PackageType_ProjectCode.pdf
      const clientName = (clientNames[record.UserID] || '').replace(/\s+/g, '_');
      const packageName = (packageTypes.find(pkg => pkg.PackageID === record.PackageID)?.PackageName || '').replace(/\s+/g, '_');
      const projectCode = (record.ProjectCode || '').replace(/\s+/g, '_');
      const filename = `${clientName}_${packageName}_${projectCode}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      message.error('Failed to download PDF');
    } finally {
      setDownloadingId(null);
    }
  };
  const moreMenu = (record) => (
    <Menu>
      <Menu.Item
        icon={<EditOutlined />}
        onClick={() => navigate(`/quotation-details`, { state: { quotationId: record.QuotationID } })}
      >
        Client Details
      </Menu.Item>
      <Menu.Item
        icon={<EyeOutlined />}
        onClick={() => navigate(`/quotation-view`, { state: { quotationId: record.QuotationID } })}
      >
        Quotation Details
      </Menu.Item>
      <Menu.Item
        icon={<FileDoneOutlined />}
        onClick={() => handleDownloadPDF(record, false)}
      >
        PDF for Send
      </Menu.Item>
      <Menu.Item
        icon={<PrinterOutlined />}
        onClick={() => handleDownloadPDF(record, true)}
      >
        Pdf for Print
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        icon={<CopyOutlined />}
        onClick={async () => {
          try {
            message.loading({ content: 'Duplicating...', key: 'duplicate' });
            await copyQuotation(record.QuotationID);
            message.success({ content: 'Quotation duplicated', key: 'duplicate', duration: 2 });
            fetchQuotations();
          } catch (error) {
            message.error('Failed to duplicate quotation');
          }
        }}
      >
        Duplicate
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        icon={<DeleteOutlined />}
        danger
        onClick={() => confirmDeleteQuotation(record)}
      >
        Delete
      </Menu.Item>
    </Menu>
  );

  const onRowContextMenu = (record) => {
    return {
      onContextMenu: (event) => {
        event.preventDefault();
        Dropdown.render(
          moreMenu(record),
          {
            x: event.clientX,
            y: event.clientY,
          }
        );
      },
    };
  };
  const columns = [
    {
      title: 'Client Name',
      key: 'clientName',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Text>{clientNames[record.UserID] || 'Loading...'}</Text>
        </div>
      ),
      width: 180
    },
    {
      title: 'Package Type',
      key: 'PackageType',
      render: (_, record) => (
        <Text>
          {console.log(record.PackageID)}
          {packageTypes.find(pkg => pkg.PackageID === record.PackageID)?.PackageName || 'Loading...'}
        </Text>
      ),
      width: 140
    },
    {
      title: 'Project Name',
      dataIndex: 'ProjectName',
      key: 'ProjectName',
      render: (text) => (
        <Text strong>{text}</Text>
      ),
      width: 180
    },
    {
      title: 'Project Code',
      dataIndex: 'ProjectCode',
      key: 'ProjectCode',
      render: (text) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{text}</Text>
      ),
      width: 120
    },
    {
      title: 'Issue Date',
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'center',
      width: 120
    },
    {
      title: 'Menu',
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Dropdown overlay={moreMenu(record)} trigger={["click"]}>
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
      width: 80
    },
  ];

  return (
    <div style={{ position: 'relative' }}>
      {downloadingId && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          flexDirection: 'column',
          gap: '16px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '32px 48px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 220
          }}>
            <Spin size="large" />
            <Typography.Text style={{ color: '#333', marginTop: 16 }}>Generating PDF...</Typography.Text>
          </div>
        </div>
      )}
      <Card
        bordered={false}
        style={{ boxShadow: 'none', borderRadius: 8 }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>Quotations</Title>
            <Text type="secondary">Manage your quotations and proposals</Text>
          </Col>
          <Col>
            <Space>
              <Search
                placeholder="Search quotations..."
                allowClear
                enterButton={<SearchOutlined />}
                size="middle"
                onSearch={value => setSearchText(value)}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 250 }}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/quotation-details')}
                style={{ borderRadius: 6 }}
              >
                New Quotation
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          dataSource={filteredQuotations}
          columns={columns}
          rowKey="QuotationID"
          loading={loading}
          pagination={false}
          scroll={{ x: '100%' }}
          outline
          bordered
          size="small"
          onRow={onRowContextMenu}
        />
      </Card>
    </div>
  );
};

export default Quotations;